import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/lib/api-client";
import type { User } from "@/api/generated/client/models/User";
import { updateUserSchema, type UpdateUserForm } from "@/schemas/user";
import { buildUpdatePayload } from "@/lib/form-utils";

export function useProfileSettings(user: User | null | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateUserForm>({
    name: "",
    phone: "",
  });

  // Update form when user data loads
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        phone: user.phone || "",
      });
    }
  }, [user]);

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateUserForm) => {
      if (!user?.id) throw new Error("User ID not found");
      
      // Build update payload with only changed string fields
      const updateData = buildUpdatePayload<UpdateUserForm>(
        { name: data.name, phone: data.phone },
        { name: user.name, phone: user.phone },
        {
          name: { required: true }, // Name is required and cannot be empty
          phone: {}, // Phone is optional and can be cleared
        }
      );
      
      // If no fields to update, throw error
      if (Object.keys(updateData).length === 0) {
        throw new Error("No changes to save");
      }
      
      // Validate the data against the schema
      const validatedData = updateUserSchema.parse(updateData);
      
      // Use correct API client signature: updateUser({ userId, updateUserRequest })
      const response = await apiClient.users.updateUser({
        userId: user.id,
        updateUserRequest: validatedData,
      });
      
      return response;
    },
    onSuccess: (updatedUser) => {
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });
      
      // Update the query cache with the new user data
      if (updatedUser && typeof updatedUser === 'object' && 'id' in updatedUser) {
        const updated = updatedUser as User;
        
        // Update the user query cache directly
        queryClient.setQueryData(["user", updated.firebaseUid], updated);
        queryClient.setQueryData(["/api/users/me"], updated);
      }
      
      // Invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["/api/users/me"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      // Handle Zod validation errors with detailed messages
      let errorMessage = error.message || "Failed to update profile. Please try again.";
      
      if (error.name === "ZodError") {
        // Extract first validation error message
        errorMessage = "Please check your input: " + error.message;
      }
      
      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || "",
      phone: user?.phone || "",
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return {
    isEditing,
    formData,
    setFormData,
    handleSubmit,
    handleCancel,
    handleEdit,
    updateMutation,
  };
}
